import { useState } from "react";

import { Loader2 } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  type MovieSummary,
  useMovieRepository,
} from "@/pages/movies/hooks/use-movie-repository";
import { CoalescedResolveBanner } from "@/pages/tina/items/components/panels/hitl-panel/coalescing";
import { readCinemaScreeningContext } from "@/pages/tina/items/components/panels/hitl-panel/hitl-readers";
import type { ResolutionComponentProps } from "@/pages/tina/items/components/panels/hitl-panel/resolution-components";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import type { HitlResolutionInput } from "@/types/tina";

type Mode = "pick" | "create";

const MODES: ReadonlyArray<{ mode: Mode; label: string }> = [
  { mode: "pick", label: "Use an existing movie" },
  { mode: "create", label: "Create from a TMDB link or ID" },
];

/**
 * Search-result row label: title, then year and director inline so curators can
 * tell same-titled movies apart at a glance — e.g. `Oppenheimer (2023) —
 * Christopher Nolan`. The year/director segments drop out when unknown.
 */
export function movieOptionLabel(movie: MovieSummary): string {
  const year = movie.releaseYear ? ` (${movie.releaseYear})` : "";
  const director = movie.director ? ` — ${movie.director}` : "";
  return `${movie.name}${year}${director}`;
}

export function MovieResolution({
  item,
  hitlContext,
  onResolved,
}: ResolutionComponentProps) {
  const { resolveHitl } = useItemsRepository();
  const { search } = useMovieRepository();
  const [mode, setMode] = useState<Mode>("pick");
  const [picked, setPicked] = useState<MovieSummary | undefined>();
  const [tmdbRef, setTmdbRef] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const movieTitle = readCinemaScreeningContext(item, hitlContext)?.movieTitle;
  const canSubmit =
    mode === "pick" ? Boolean(picked?.id) : tmdbRef.trim().length > 0;

  async function handleSubmit() {
    const resolution: HitlResolutionInput =
      mode === "pick"
        ? { kind: "movie.pick", movie_id: picked!.id }
        : { kind: "movie.create_from_tmdb", tmdb_ref: tmdbRef.trim() };
    setSubmitting(true);
    try {
      await resolveHitl(item.id, { resolution });
      onResolved();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <CoalescedResolveBanner item={item} />
      {movieTitle && (
        <p className="text-sm text-muted-foreground">
          Extracted movie:{" "}
          <span className="font-medium text-foreground">{movieTitle}</span>
        </p>
      )}

      <div className="flex flex-wrap gap-1">
        {MODES.map(({ mode: m, label }) => (
          <Button
            key={m}
            type="button"
            size="sm"
            variant={mode === m ? "default" : "outline"}
            onClick={() => setMode(m)}
          >
            {label}
          </Button>
        ))}
      </div>

      {mode === "pick" && (
        <RemoteSelect<MovieSummary>
          value={picked}
          triggerType="change"
          showSelected
          onValueChanged={(selected) => setPicked(selected)}
          accessorFn={(option) => ({
            id: option?.id || "",
            name: option ? movieOptionLabel(option) : "",
            iconUrl: option?.posterUrl,
          })}
          onSearch={async (query, onStatusChanged) => {
            onStatusChanged("loading");
            const results = (await search(query)) as MovieSummary[];
            onStatusChanged("success");
            return results;
          }}
        />
      )}
      {mode === "create" && (
        <div className="flex flex-col gap-1">
          <Input
            value={tmdbRef}
            onChange={(event) => setTmdbRef(event.target.value)}
            placeholder="https://www.themoviedb.org/movie/… or a numeric TMDB id"
          />
          <span className="text-xs text-muted-foreground">
            Paste a themoviedb.org link or a numeric TMDB id. The movie is
            created on resume if it isn't already in Mood.
          </span>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <Button
          type="button"
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
        >
          {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Resolve &amp; resume
        </Button>
        <span className="text-xs text-muted-foreground">
          Re-runs movie_resolution with your choice and continues to create.
        </span>
      </div>
    </div>
  );
}
